import { useApolloClient } from '@apollo/client';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  GET_ORDER_EDITING_WORKSPACE,
  GET_ORDER_EDITING_CATALOG,
} from 'gql/queries';
import {
  RECONCILE_ORDER_EDITING_OPERATION,
  REVIEW_ORDER_EDITING_REQUEST,
  CONFIGURE_ORDER_EDITING_INTEGRATION,
  ACKNOWLEDGE_ORDER_EDITING_ROUTING,
  SAVE_ORDER_EDITING_SETTINGS,
} from 'gql/mutations';
import {
  OrderEditingPeriod,
  OrderEditingSettings,
  OrderEditingWorkspace,
  OrderEditingIntegrationInput,
  OrderEditingIntegrationResult,
  OrderEditingCatalogInput,
  OrderEditingCatalogPage,
} from 'types/order-editing';
import {
  normalizeOrderEditingSettings,
  prepareExpandedSettings,
  validateExpandedSettings,
} from './expandedSettings';

export function useOrderEditingWorkspace(
  storeId: string | null | undefined,
  options: { period?: OrderEditingPeriod; enabled?: boolean } = {},
) {
  const enabled = options.enabled ?? true;
  const periodStart = options.period?.periodStart;
  const periodEnd = options.period?.periodEnd;
  const client = useApolloClient();
  const selectedStore = useRef(storeId);
  selectedStore.current = storeId;
  const generation = useRef(0);
  // Lock synchronously, before React renders disabled controls for this request.
  const requestInFlight = useRef(false);
  const activePeriod = useRef<OrderEditingPeriod>();
  const [workspace, setWorkspace] = useState<OrderEditingWorkspace>();
  const [draft, setDraft] = useState<OrderEditingSettings>();
  const [revision, setRevision] = useState(0);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [checking, setChecking] = useState<string>();
  const [reviewing, setReviewing] = useState<string>();
  const [configuring, setConfiguring] = useState(false);
  const [acknowledging, setAcknowledging] = useState<string>();
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [conflict, setConflict] = useState(false);

  const load = useCallback(
    async (replaceDraft = false, period?: OrderEditingPeriod) => {
      if (!storeId || !enabled || requestInFlight.current) return;
      requestInFlight.current = true;
      const requestGeneration = ++generation.current;
      const current = () =>
        selectedStore.current === storeId &&
        generation.current === requestGeneration;
      setLoading(true);
      setError('');
      try {
        const response = await client.query<{
          orderEditingWorkspace: OrderEditingWorkspace;
        }>({
          query: GET_ORDER_EDITING_WORKSPACE,
          variables: {
            input: { storeId, ...(period || activePeriod.current) },
          },
          fetchPolicy: 'network-only',
        });
        if (!current()) return;
        const received = response.data?.orderEditingWorkspace;
        if (!received || received.storeId !== storeId)
          throw new Error('Missing or unexpected workspace');
        const next = {
          ...received,
          settings: normalizeOrderEditingSettings(received.settings),
        };
        activePeriod.current = {
          periodStart: next.usage.periodStart,
          periodEnd: next.usage.periodEnd,
        };
        setWorkspace(next);
        if (replaceDraft) {
          setDraft(next.settings);
          setRevision(next.revision);
          setConflict(false);
          setNotice('');
        }
        return next;
      } catch (_) {
        if (current()) setError('Unable to load Order Editing. Please retry.');
      } finally {
        if (current()) {
          requestInFlight.current = false;
          setLoading(false);
        }
      }
    },
    [client, storeId, enabled],
  );

  useEffect(() => {
    requestInFlight.current = false;
    const period =
      periodStart && periodEnd ? { periodStart, periodEnd } : undefined;
    activePeriod.current = period;
    setWorkspace(undefined);
    setDraft(undefined);
    setError('');
    setNotice('');
    setConflict(false);
    setSaving(false);
    setChecking(undefined);
    setReviewing(undefined);
    setConfiguring(false);
    setAcknowledging(undefined);
    if (enabled) void load(true, period);
    return () => {
      generation.current += 1;
    };
  }, [load, enabled, periodStart, periodEnd]);

  const save = async (): Promise<boolean> => {
    if (
      !storeId ||
      !draft ||
      workspace?.storeId !== storeId ||
      requestInFlight.current
    )
      return false;
    setNotice('');
    if (
      !Number.isInteger(draft.deadlineMinutes) ||
      draft.deadlineMinutes < 1 ||
      draft.deadlineMinutes > 14400
    ) {
      setError(
        'Enter a whole number from 1 to 14,400 minutes for the editing window.',
      );
      return false;
    }
    const expandedError = validateExpandedSettings(draft);
    if (expandedError) {
      setError(expandedError);
      return false;
    }
    if (
      Object.values(draft.appearance).some(
        (value) =>
          /<[^>]*>/.test(value) ||
          Array.from(String(value)).some(
            (character) =>
              character.charCodeAt(0) < 32 || character.charCodeAt(0) === 127,
          ),
      )
    ) {
      setError(
        'Use plain text without HTML or control characters in customer wording.',
      );
      return false;
    }
    const requestedStore = storeId;
    requestInFlight.current = true;
    const requestedGeneration = generation.current;
    const current = () =>
      selectedStore.current === requestedStore &&
      generation.current === requestedGeneration;
    setSaving(true);
    setError('');
    try {
      const response = await client.mutate<{
        saveOrderEditingSettings: Pick<
          OrderEditingWorkspace,
          'storeId' | 'revision' | 'settings'
        >;
      }>({
        mutation: SAVE_ORDER_EDITING_SETTINGS,
        variables: {
          input: {
            storeId,
            revision,
            settings: prepareExpandedSettings(draft),
          },
        },
      });
      if (!current()) return false;
      const saved = response.data?.saveOrderEditingSettings;
      if (!saved || saved.storeId !== requestedStore)
        throw new Error('Unexpected store');
      saved.settings = normalizeOrderEditingSettings(saved.settings);
      setDraft(saved.settings);
      setRevision(saved.revision);
      setWorkspace((previous) => previous && { ...previous, ...saved });
      setConflict(false);
      setNotice('Settings saved.');
      return true;
    } catch (reason: any) {
      if (current()) {
        const isConflict =
          reason?.graphQLErrors?.some((item: any) =>
            String(item.extensions?.code || '').includes('CONFLICT'),
          ) || String(reason?.message || '').includes('CONFLICT');
        setConflict(!!isConflict);
        setError(
          isConflict
            ? 'These settings changed since you opened them. Your draft is preserved. Reload the latest settings before saving again.'
            : 'Unable to save settings. Your draft is preserved. Please retry.',
        );
      }
      return false;
    } finally {
      if (current()) {
        requestInFlight.current = false;
        setSaving(false);
      }
    }
  };

  const reconcile = async (operationId: string) => {
    if (!storeId || requestInFlight.current) return;
    requestInFlight.current = true;
    const requestedStore = storeId;
    const requestedGeneration = generation.current;
    const current = () =>
      selectedStore.current === requestedStore &&
      generation.current === requestedGeneration;
    setChecking(operationId);
    setError('');
    setNotice('');
    try {
      await client.mutate({
        mutation: RECONCILE_ORDER_EDITING_OPERATION,
        variables: { input: { storeId, operationId } },
      });
      if (!current()) return;
      setNotice(
        'Status checked. Pending results may still need time to complete.',
      );
      setChecking(undefined);
      requestInFlight.current = false;
      await load(false);
    } catch (_) {
      if (current())
        setError('Unable to check operation status. Please retry.');
    } finally {
      if (current()) {
        requestInFlight.current = false;
        setChecking(undefined);
      }
    }
  };

  const reviewRequest = async (
    requestId: string,
    decision: 'approve' | 'reject',
    note: string,
    fulfillmentPaused?: boolean,
  ) => {
    if (!storeId || requestInFlight.current) return;
    requestInFlight.current = true;
    const requestedGeneration = generation.current;
    const current = () =>
      selectedStore.current === storeId &&
      generation.current === requestedGeneration;
    setReviewing(requestId);
    setError('');
    setNotice('');
    try {
      await client.mutate({
        mutation: REVIEW_ORDER_EDITING_REQUEST,
        variables: {
          input: {
            storeId,
            requestId,
            decision,
            note,
            ...(fulfillmentPaused !== undefined ? { fulfillmentPaused } : {}),
          },
        },
      });
      if (!current()) return;
      setNotice(
        decision === 'approve'
          ? 'Request approved. An edit still needs customer confirmation while eligible; a support request is marked resolved.'
          : 'Request rejected.',
      );
      setReviewing(undefined);
      requestInFlight.current = false;
      await load(false);
    } catch (_) {
      if (current())
        setError(
          'Unable to review this request. Refresh its status before trying again.',
        );
    } finally {
      if (current()) {
        requestInFlight.current = false;
        setReviewing(undefined);
      }
    }
  };

  const acknowledgeRouting = async (operationId: string) => {
    if (!storeId || requestInFlight.current) return;
    requestInFlight.current = true;
    const requestedGeneration = generation.current;
    const current = () =>
      selectedStore.current === storeId &&
      generation.current === requestedGeneration;
    setAcknowledging(operationId);
    setError('');
    setNotice('');
    try {
      await client.mutate({
        mutation: ACKNOWLEDGE_ORDER_EDITING_ROUTING,
        variables: { input: { storeId, operationId } },
      });
      if (!current()) return;
      setNotice(
        'Service confirmation recorded. Check fulfillment hold status below.',
      );
      setAcknowledging(undefined);
      requestInFlight.current = false;
      await load(false);
    } catch (_) {
      if (current())
        setError(
          'Unable to confirm the service. Verify payment and event delivery status, then refresh before trying again.',
        );
    } finally {
      if (current()) {
        requestInFlight.current = false;
        setAcknowledging(undefined);
      }
    }
  };

  const configureIntegration = async (
    input: OrderEditingIntegrationInput,
  ): Promise<OrderEditingIntegrationResult | undefined> => {
    if (!storeId || requestInFlight.current) return;
    requestInFlight.current = true;
    const requestedGeneration = generation.current;
    const current = () =>
      selectedStore.current === storeId &&
      generation.current === requestedGeneration;
    setConfiguring(true);
    try {
      const response = await client.mutate<{
        configureOrderEditingIntegration: OrderEditingIntegrationResult;
      }>({
        mutation: CONFIGURE_ORDER_EDITING_INTEGRATION,
        variables: { input: { storeId, ...input } },
      });
      if (!current()) return;
      const result = response.data?.configureOrderEditingIntegration;
      if (!result?.summary) throw new Error('Missing integration result');
      setWorkspace(
        (previous) => previous && { ...previous, integration: result.summary },
      );
      return result;
    } catch (reason) {
      if (current()) throw reason;
    } finally {
      if (current()) {
        requestInFlight.current = false;
        setConfiguring(false);
      }
    }
  };

  const visibleWorkspace =
    workspace?.storeId === storeId ? workspace : undefined;
  const visibleDraft = visibleWorkspace ? draft : undefined;
  const loadCatalog = useCallback(
    async (input: OrderEditingCatalogInput) => {
      if (!storeId) throw new Error('Select a store to browse products.');
      const response = await client.query<{
        orderEditingCatalog: OrderEditingCatalogPage;
      }>({
        query: GET_ORDER_EDITING_CATALOG,
        variables: { input: { storeId, ...input } },
        fetchPolicy: 'network-only',
      });
      const catalog = response.data?.orderEditingCatalog;
      if (selectedStore.current !== storeId || !catalog)
        throw new Error('The catalog is no longer available.');
      return catalog;
    },
    [client, storeId],
  );
  return {
    loadCatalog,
    workspace: visibleWorkspace,
    draft: visibleDraft,
    setDraft,
    revision,
    loading,
    saving,
    checking,
    reviewing,
    configuring,
    acknowledging,
    error,
    setError,
    notice,
    conflict,
    save,
    reconcile,
    reviewRequest,
    configureIntegration,
    acknowledgeRouting,
    load,
    dirty:
      !!visibleDraft &&
      JSON.stringify(visibleDraft) !==
        JSON.stringify(visibleWorkspace?.settings),
  };
}
